import { useLayoutEffect, useRef, useState } from "react";

/**
 * 자식 셀 중 **하나라도 줄바꿈되면 전부 세로 배치**로 전환해야 하는지 판정한다(#5290).
 *
 * ## 왜 CSS 로 못 하는가
 *
 * 시안(29187:45988)은 랩 케이스에서 **세 항목 모두** 아이콘 위 / 텍스트 아래다 —
 * "Garantie"(8자)처럼 한 줄에 들어가는 것까지 세로다. 즉 조건이 "이 셀이 랩됐나" 가 아니라
 * **"행에 랩된 셀이 있나"** 다.
 *
 * CSS 에는 형제의 레이아웃 결과에 반응하는 선택자가 없다. 조건이 컨테이너 폭이 아니라
 * **텍스트 길이**(locale)라서 container query 로도 안 된다. 그래서 측정한다.
 *
 * ## 판정 방법과 그 이유
 *
 * 각 셀은 `flex-wrap` 이므로, 라벨이 아이콘 옆에 못 들어가면 **다음 flex 줄로 내려간다.**
 * 그 상태를 "라벨의 top 이 아이콘의 bottom 이하인가" 로 읽는다 — 글자수 임계값 같은 추정이
 * 아니라 브라우저가 실제로 계산한 결과다(폰트 로드·자간·locale 전부 반영됨).
 *
 * ⚠️ **측정은 반드시 `false`(가로 배치) 상태에서 해야 한다.** 세로로 전환된 뒤에 재면
 * 라벨이 항상 아이콘 아래에 있어 "랩됨" 으로 읽히고, 한 번 세로가 되면 영원히 세로로
 * 고정된다. 그래서 매 측정 전에 `false` 로 되돌린 뒤 다음 프레임에 읽는다.
 *
 * ⚠️ `ResizeObserver` 는 **폭 변화만** 트리거로 쓴다. 배치가 바뀌면 높이가 변하는데
 * (36 → 72) 높이까지 보면 `측정 → 배치 변경 → 높이 변화 → 재측정` 무한 루프가 된다.
 * 같은 클래스의 사례가 하나 더 있다 —
 * `docs/pitfalls.md` #44(측정이 배치를 바꾸는 훅).
 *
 * ## 알고 받아들인 것
 *
 * 서버 HTML 은 측정 없이 나가므로 **항상 가로 배치**다. 세로를 기본값으로 두면 반대로
 * en·ko·jp·cn(대다수)이 어긋난 상태로 나가므로 이쪽이 맞다.
 *
 * 교정은 layout effect 안에서 **동기로** 끝내 페인트 전에 확정한다 → 깜빡임 없음.
 * 스켈레톤으로 가리는 방법도 있지만, 세로가 필요한 건 de·es·fr 뿐인데 **전 locale 의**
 * 콘텐츠를 늦추게 되어 손해가 더 크다.
 *
 * @param deps 라벨 문자열들. locale 이 바뀌면 길이가 달라지므로 재측정 트리거가 된다.
 */
export function useUniformWrap(deps: unknown[]) {
  const ref = useRef<HTMLDivElement>(null);
  const [stacked, setStacked] = useState(false);
  /** 마지막으로 측정한 폭. 높이 변화로 인한 재측정을 걸러낸다. */
  const lastWidth = useRef(0);

  // ⚠️ `useLayoutEffect` 다. `useEffect` 로 두면 측정이 **페인트 후**에 일어나서 긴 라벨
  // locale(de·es·fr)에서 가로 → 세로 전환이 한 프레임 보인다(새로고침 시 눈에 띈다).
  // 첫 측정은 되돌릴 필요가 없으므로(이미 가로 배치) **동기로** 읽어 페인트 전에 확정한다.
  //
  // 서버에서는 실행되지 않는다(effect). 첫 HTML 은 항상 가로 배치이고, 하이드레이션 직후
  // 페인트 전에 교정된다.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    let raf = 0;

    const read = () =>
      Array.from(el.children).some((cell) => {
        const icon = cell.firstElementChild;
        const label = cell.lastElementChild;
        if (!icon || !label || icon === label) return false;
        // ⚠️ `offsetTop`/`offsetHeight` 를 쓰면 안 된다 — 아이콘이 `<svg>` 라 그 속성이
        // **없다**(HTMLElement 전용). `undefined` 산술이 NaN 이 되어 비교가 항상 false 로
        // 떨어지고, 훅이 조용히 아무 일도 안 한다(실측: 7 locale 전부 `stacked=false`).
        // 1px 여유는 소수점 레이아웃 대비.
        return (
          label.getBoundingClientRect().top >=
          icon.getBoundingClientRect().bottom - 1
        );
      });

    const measure = () => {
      // 가로 배치로 되돌린 다음 프레임에 읽는다(위 ⚠️ 참조).
      setStacked(false);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setStacked(read()));
    };

    // 첫 측정만 **동기**다 — 마운트 시점의 DOM 은 이미 가로 배치(`stacked=false`)라
    // 되돌릴 필요가 없고, layout effect 안에서 즉시 확정하면 깜빡임이 아예 없다.
    // (`measure()` 의 reset + rAF 는 **이미 세로인 상태에서 다시 재야 하는** 리사이즈용이다.)
    lastWidth.current = el.getBoundingClientRect().width;
    setStacked(read());

    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (Math.abs(width - lastWidth.current) < 1) return;
      lastWidth.current = width;
      measure();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ref, stacked };
}
